/**
 * P32 (Pedro 2026-10-12): the Welcome → SAA period control, shaped like the AuM & Return
 * selector (`periodRange.tsx`) but in years, because the SAA series run for decades:
 * Last 3/5/7/10/15/20/25yr, Max, and Range (a From and a To month). Default Last 20yr.
 *
 * "Last N yr" is resolved on the SERVER, per profile, ending at that profile's own latest
 * stored day (`years=N`, `saa_analytics.last_years_window`) — the data-span anchoring of
 * C-261. Max = each profile's full history. Range = a From DAY and a To DAY (Pedro
 * 2026-10-13: day pickers, not month pickers), both limited to the stored data's span;
 * the server also clamps a window to the data, so the reported period is the real one.
 */
import { Group, SegmentedControl } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';

export type SaaPeriodPreset = '3y' | '5y' | '7y' | '10y' | '15y' | '20y' | '25y' | 'max' | 'range';

export const SAA_DEFAULT_PRESET: SaaPeriodPreset = '20y';

const YEAR_PRESETS: SaaPeriodPreset[] = ['3y', '5y', '7y', '10y', '15y', '20y', '25y'];

/** The analytics query for a choice: `years`, or a from / to pair, or nothing (Max).
 *  Range with a month missing (or To before From) returns null = not ready to query. */
export function saaPeriodQuery(
  preset: SaaPeriodPreset, rangeFrom: Date | null, rangeTo: Date | null,
): { years?: number; from?: string; to?: string } | null {
  if (preset === 'max') return {};
  if (preset !== 'range') return { years: Number(preset.slice(0, -1)) };
  if (!rangeFrom || !rangeTo || rangeTo < rangeFrom) return null;
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(rangeFrom), to: iso(rangeTo) };
}

/** 'YYYY-MM-DD' → a local Date (no time-zone shift), or undefined. */
export function dayOf(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function SaaPeriodSelect({
  preset, onPreset, rangeFrom, onRangeFrom, rangeTo, onRangeTo, dataFirst, dataLast,
}: {
  preset: SaaPeriodPreset;
  onPreset: (p: SaaPeriodPreset) => void;
  rangeFrom: Date | null;
  onRangeFrom: (d: Date | null) => void;
  rangeTo: Date | null;
  onRangeTo: (d: Date | null) => void;
  /** The stored data's span for the ticked profiles (earliest first / latest last day). */
  dataFirst?: Date;
  dataLast?: Date;
}) {
  return (
    <Group align="flex-end" wrap="wrap" gap="md">
      <SegmentedControl
        size="xs"
        value={preset}
        onChange={(v) => onPreset(v as SaaPeriodPreset)}
        data={[
          ...YEAR_PRESETS.map((p) => ({ label: `Last ${p.slice(0, -1)}yr`, value: p })),
          { label: 'Max', value: 'max' },
          { label: 'Range…', value: 'range' },
        ]}
      />
      {preset === 'range' && (
        <>
          <DatePickerInput size="xs" label="From" placeholder="Start date" valueFormat="DD/MM/YYYY"
            value={rangeFrom} onChange={onRangeFrom} maw={150}
            minDate={dataFirst} maxDate={rangeTo ?? dataLast} />
          <DatePickerInput size="xs" label="To" placeholder="End date" valueFormat="DD/MM/YYYY"
            value={rangeTo} onChange={onRangeTo} maw={150}
            minDate={rangeFrom ?? dataFirst} maxDate={dataLast} />
        </>
      )}
    </Group>
  );
}
